// Deterministic JSON serializer used for hashing and recipe encoding.
//
// Properties:
//   - object keys are sorted alphabetically (recursively)
//   - no insignificant whitespace
//   - arrays preserve order
//   - `undefined` is omitted (matches JSON.stringify behavior)
//   - rejects non-finite numbers (NaN, +/- Infinity) to avoid silently lossy
//     round-trips
//   - rejects symbols and functions
//
// Output is stable for any structurally equivalent input. Two calls with the
// same payload produce byte-identical results.

export function canonicalJson(value: unknown): string {
  return serialize(value);
}

function serialize(v: unknown): string {
  if (v === null) return 'null';
  const t = typeof v;
  switch (t) {
    case 'boolean':
      return v ? 'true' : 'false';
    case 'number': {
      const n = v as number;
      if (!Number.isFinite(n)) {
        throw new Error(`canonicalJson: non-finite number ${n}`);
      }
      // Avoid `-0` collapsing to `0` differently across engines.
      if (Object.is(n, -0)) return '0';
      return JSON.stringify(n);
    }
    case 'string':
      return JSON.stringify(v);
    case 'bigint':
      throw new Error('canonicalJson: BigInt not supported');
    case 'symbol':
    case 'function':
      throw new Error(`canonicalJson: ${t} not supported`);
    case 'undefined':
      throw new Error('canonicalJson: undefined not supported at root');
    case 'object': {
      if (Array.isArray(v)) {
        const items = v.map((item) => {
          if (item === undefined) return 'null'; // JSON.stringify behavior
          return serialize(item);
        });
        return `[${items.join(',')}]`;
      }
      const obj = v as Record<string, unknown>;
      const keys = Object.keys(obj).sort();
      const pairs: string[] = [];
      for (const k of keys) {
        const child = obj[k];
        if (child === undefined) continue;
        pairs.push(`${JSON.stringify(k)}:${serialize(child)}`);
      }
      return `{${pairs.join(',')}}`;
    }
    default:
      throw new Error(`canonicalJson: unsupported value of type ${t}`);
  }
}
